import { useState } from 'react';
import Lightbox from './Lightbox';

import { homePagePhotos, photos, photoTags } from '../photos';

export default function Gallery({ fullPage = false }: { fullPage?: boolean }) {
  const [activeTag, setActiveTag] = useState('All photos');
  const pagePhotos = fullPage ? photos : homePagePhotos;
  const images =
    fullPage && activeTag !== 'All photos'
      ? pagePhotos.filter((photo) => photo.tags.includes(activeTag))
      : pagePhotos;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () =>
    setLightboxIndex((i) =>
      i !== null ? (i - 1 + images.length) % images.length : null
    );
  const nextImage = () =>
    setLightboxIndex((i) => (i !== null ? (i + 1) % images.length : null));

  return (
    <>
      {fullPage && (
        <div className="mb-8">
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Filter photos by tag"
          >
            {['All photos', ...photoTags].map((tag) => (
              <button
                key={tag}
                type="button"
                aria-pressed={activeTag === tag}
                onClick={() => {
                  setActiveTag(tag);
                  setLightboxIndex(null);
                }}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2 ${activeTag === tag ? 'border-forest bg-forest text-white' : 'border-forest/20 bg-white text-forest hover:bg-glacial-light'}`}
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="mt-4 text-sm text-bark/60" role="status">
            {images.length} {images.length === 1 ? 'photo' : 'photos'} ·{' '}
            {activeTag}
          </p>
        </div>
      )}
      <div
        className={
          fullPage
            ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3'
            : 'flex gap-4 overflow-x-auto pb-4 scrollbar-hide'
        }
      >
        {images.map((img, index) => (
          <figure
            key={img.src}
            className="flex-none cursor-pointer overflow-hidden rounded-2xl border border-forest/10 bg-white shadow-xs transition hover:shadow-md"
          >
            <button
              type="button"
              onClick={() => openLightbox(index)}
              className="block w-full focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2"
              aria-label={`View ${img.alt} full size`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className={
                  fullPage
                    ? 'h-72 w-full object-cover'
                    : 'h-64 w-80 object-cover'
                }
                loading="lazy"
                decoding="async"
              />
            </button>
            {fullPage && (
              <figcaption className="p-5">
                <h2 className="font-display text-xl font-semibold text-forest">
                  {img.title}
                </h2>
                <ul
                  className="mt-3 flex flex-wrap gap-2"
                  aria-label="Photo tags"
                >
                  {img.tags.map((tag) => (
                    <li
                      key={tag}
                      className="rounded-full bg-forest/5 px-2.5 py-1 text-xs text-forest"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              </figcaption>
            )}
          </figure>
        ))}
      </div>
      {!fullPage && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-bark/55">
            Lake days, evening skies, and woodland visitors.
          </p>
          <a
            href="/photos/"
            className="rounded-xl bg-forest px-5 py-3 text-sm font-semibold text-white transition hover:bg-forest-hover focus:outline-hidden focus:ring-2 focus:ring-forest focus:ring-offset-2"
          >
            Explore all photos →
          </a>
        </div>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </>
  );
}
